import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Schema } from 'yup';

import { Enum } from '~/data/enums';
import { SalaryProposalData } from '~/data/proposals';
import {
  ProposalAttachmentFormData,
  mapDefaultDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { convertToDateOrNull } from '~/utils/helpers/date';

export type SalaryProposalFormData_Common = {
  employmentId: string;
  salaryFrequency: string;
  salaryType: string;
  salaryClass: string;
  changeReason: string;
  info: string;
  responsibleHR: number | string;
} & ProposalAttachmentFormData;

type NumberFieldValueType = number | '' | undefined;

export type SalaryProposalFormData_Standard = {
  baseSalary: number;
  raiseAmount: NumberFieldValueType;
  raisePercentage: NumberFieldValueType;
  newSalary: NumberFieldValueType;
  startDate?: Date | null;
  endDate?: Date | null;
};

export type SalaryProposalHourlyComponentFormData = {
  hourlySalaryEnum: Enum | undefined;
  startDate?: Date | null;
  amount?: NumberFieldValueType;
};

export type SalaryProposalFormData_HourlyComponents = {
  hourlySalaryComponents?: SalaryProposalHourlyComponentFormData[];
};

export type SalaryProposalFormData = SalaryProposalFormData_Common &
  SalaryProposalFormData_Standard &
  SalaryProposalFormData_HourlyComponents;

export type CurrentEmploymentSalary = {
  employmentId?: number;
  salaryFrequency: string;
  baseSalary: number;
};

export const mapDefaultData = (
  targetEmploymentId: number,
  currentEmploymentSalary: CurrentEmploymentSalary,
  hourlySalaryComponents: Enum[],
  enableHourlySalaryComponents?: boolean,
  proposalData?: SalaryProposalData,
  defaultResponsibleHrId?: number,
): SalaryProposalFormData => {
  const {
    defaultToEmptyString,
    defaultToNumber,
    defaultResponsibleHR,
    defaultToNumberField,
  } = mapDefaultDataUtils();

  const mapHourlySalaryComponent = (
    components: SalaryProposalData['components'] | undefined,
    index: number,
  ): SalaryProposalHourlyComponentFormData => {
    const getComponentKey = (
      propName: 'Amount' | 'StartDate',
    ): keyof SalaryProposalData['components'] => {
      return `comp${
        index + 1
      }${propName}` as keyof SalaryProposalData['components'];
    };

    const startDate = components?.[getComponentKey('StartDate')];
    const amount = components?.[getComponentKey('Amount')];

    return {
      hourlySalaryEnum: hourlySalaryComponents[index],
      startDate: convertToDateOrNull(startDate),
      amount: defaultToNumberField(amount, ''),
    };
  };

  // TODO: Investigate if Hourly Salary Components count should be always 4 (legacy) or dynamic (based on # of enums)
  const defaultSalaryComponents = [1, 2, 3, 4]; // hourlySalaryComponents

  return {
    employmentId: defaultToEmptyString(
      proposalData?.employmentFK || targetEmploymentId,
    ),
    salaryFrequency:
      proposalData?.frequencyFK || currentEmploymentSalary.salaryFrequency,
    baseSalary: currentEmploymentSalary.baseSalary,
    raiseAmount: defaultToNumberField(proposalData?.proposedChange, ''),
    raisePercentage: defaultToNumberField(proposalData?.proposedChangePct, ''),
    newSalary: defaultToNumberField(proposalData?.proposedSalary, ''),
    startDate: convertToDateOrNull(proposalData?.startDate),
    endDate: convertToDateOrNull(proposalData?.endDate),
    salaryType: defaultToEmptyString(proposalData?.proposedSalaryTypeFK),
    salaryClass: defaultToEmptyString(proposalData?.proposedSalaryClassFK),
    changeReason: defaultToEmptyString(proposalData?.reasonFK),
    info: defaultToEmptyString(proposalData?.info),
    attachmentId: defaultToNumber(proposalData?.attachmentId, -1),
    attachmentName: defaultToEmptyString(proposalData?.attachmentName),
    attachment: undefined,
    responsibleHR: defaultResponsibleHR(
      defaultResponsibleHrId,
      defaultResponsibleHrId,
    ),
    hourlySalaryComponents: defaultSalaryComponents.map((_, i) =>
      mapHourlySalaryComponent(proposalData?.components, i),
    ),
  };
};

export const useValidationSchema = (props: {
  enableHourlySalaryComponents: boolean;
  currentEmploymentSalary: CurrentEmploymentSalary;
  filterActiveEmploymentSalary: (
    employmentId: string | number,
    currentEmploymentSalary: CurrentEmploymentSalary,
  ) => CurrentEmploymentSalary;
  hasUpcomingEmployments: boolean;
}) => {
  const {
    enableHourlySalaryComponents,
    currentEmploymentSalary,
    filterActiveEmploymentSalary,
    hasUpcomingEmployments,
  } = props;
  const { t } = useTranslation();

  const shouldValidateHourlyComponents = useCallback(
    (frequencyValue: string) => {
      return enableHourlySalaryComponents && frequencyValue === 'HOURLY';
    },
    [enableHourlySalaryComponents],
  );

  const errMsg = {
    requiredField: t('laThisRequired'),
  };

  const whenHourlyComponents = <S>(
    baseSchema: Schema<S>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onTrue?: (schema: any) => Schema<S>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFalse?: (schema: any) => Schema<S>,
  ): Schema<S> => {
    return baseSchema.when('salaryFrequency', (salaryFrequency, schema) => {
      return shouldValidateHourlyComponents(salaryFrequency)
        ? !!onTrue
          ? onTrue(schema)
          : schema
        : !!onFalse
        ? onFalse(schema)
        : schema;
    });
  };

  const yupNumberField = yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value));

  const yupSalaryField = whenHourlyComponents(
    yupNumberField,
    (s) => s,
    (s) => s.required(errMsg.requiredField),
  );

  const yupRaiseField = yupNumberField.when(
    ['salaryFrequency', 'employmentId'],
    {
      is: (salaryFrequency, employmentId) => {
        const employmentActiveSalary = filterActiveEmploymentSalary(
          employmentId,
          currentEmploymentSalary,
        );
        const raiseFieldsEnabled =
          !shouldValidateHourlyComponents(salaryFrequency) &&
          employmentActiveSalary.salaryFrequency === salaryFrequency;

        return !!raiseFieldsEnabled;
      },
      then: yupNumberField.required(errMsg.requiredField),
    },
  );

  const yupSalaryComponent = yup
    .object()
    .shape<SalaryProposalHourlyComponentFormData>({
      hourlySalaryEnum: yup.object(),
      startDate: yup
        .date()
        .nullable()
        .when('amount', (amount, schema) => {
          return !isNaN(amount)
            ? schema.required(errMsg.requiredField)
            : schema;
        }),
      amount: yupNumberField,
    })
    .required();

  return yup.object().shape<SalaryProposalFormData>({
    employmentId: hasUpcomingEmployments
      ? yup.string().required(errMsg.requiredField)
      : yup.string().default(''),
    salaryFrequency: yup.string().required(errMsg.requiredField),
    baseSalary: yup.number(),
    raiseAmount: yupRaiseField,
    raisePercentage: yupRaiseField,
    newSalary: yupSalaryField,
    startDate: whenHourlyComponents(
      yup.date().nullable(),
      (s) => s,
      (s) => s.required(errMsg.requiredField),
    ),
    endDate: yup.date().nullable(),
    salaryType: yup.string(),
    salaryClass: yup.string(),
    changeReason: yup.string().required(errMsg.requiredField),
    info: yup.string(),
    attachment: yup.object<File>().nullable(),
    attachmentId: yup.number(),
    attachmentName: yup.string(),
    responsibleHR: yup.number(),
    hourlySalaryComponents: whenHourlyComponents(
      yup.array().of(yupSalaryComponent) as Schema<
        SalaryProposalHourlyComponentFormData[] | undefined
      >,
      (s) => s.required(),
      (s) => s.notRequired(),
    ),
  });
};
